import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth';
import Card from '../components/Card';
import Button from '../components/Button';
import { useToast } from '../hooks/useToast';
import { RocketIcon, HiOutlineEye, HiOutlineEyeSlash } from '../components/Icons';
import './Login.css';

export default function Login() {
  const navigate = useNavigate();
  const toast = useToast();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);

  // Redirect if already logged in
  useEffect(() => {
    if (authService.isAuthenticated()) {
      navigate('/');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!formData.username.trim()) {
      setError('Username is required');
      return;
    }
    
    if (!formData.password) {
      setError('Password is required');
      return;
    }

    setLoading(true);

    try {
      const result = await authService.login(formData.username.trim(), formData.password);
      
      if (result.success) {
        toast.success('Login successful! Welcome back.');
        navigate('/');
        // Small delay to ensure token is stored
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } else {
        const errorMsg = result.error || 'Invalid username or password';
        setError(errorMsg);
        toast.error(errorMsg);
        console.error('Login failed:', result);
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.non_field_errors?.[0] ||
                          err.message || 
                          'An error occurred during login. Please check your connection.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(''); // Clear error when user starts typing
  };

  return (
    <div className="login-container">
      <Card className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <RocketIcon size={56} style={{ color: 'var(--primary)' }} />
          </div>
          <h1 className="login-title">Outreach CRM</h1>
          <p className="login-subtitle">Aixcellence LLC</p>
          <p className="login-description">Sign in to access your dashboard</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-error" role="alert">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
                <path
                  d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 6V10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 14H10.01"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <div className="login-field-group">
            <label 
              htmlFor="username" 
              className={`login-label ${focusedField === 'username' || formData.username ? 'focused' : ''}`}
            >
              Username
            </label>
            <div className="login-input-wrapper">
              <input
                id="username"
                type="text"
                className={`login-input ${error && !formData.username ? 'error' : ''}`}
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                onFocus={() => setFocusedField('username')}
                onBlur={() => setFocusedField(null)}
                placeholder="Enter your username"
                autoComplete="username"
                autoFocus
                disabled={loading}
              />
            </div>
          </div>

          <div className="login-field-group">
            <label 
              htmlFor="password" 
              className={`login-label ${focusedField === 'password' || formData.password ? 'focused' : ''}`}
            >
              Password
            </label>
            <div className="login-input-wrapper password-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className={`login-input ${error && !formData.password ? 'error' : ''}`}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField(null)}
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={0}
              >
                {showPassword ? (
                  <HiOutlineEyeSlash size={20} />
                ) : (
                  <HiOutlineEye size={20} />
                )}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={loading}
            style={{ width: '100%', marginTop: 'var(--spacing-xl)' }}
          >
            Sign In
          </Button>
        </form>

        <div className="login-footer">
          <p className="login-help">
            Use your Django superuser credentials to login
          </p>
          <p className="login-help-secondary">
            Having trouble? Contact your system administrator
          </p>
        </div>
      </Card>
    </div>
  );
}
